// Componente reutilizable para activar/desactivar notificaciones Push PWA
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  getPushStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/push";

export default function PushNotifSettings({ compact = false }) {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [loading, setLoading] = useState(false);
  const supported = pushSupported();

  const refresh = async () => {
    const s = await getPushStatus();
    setStatus(s);
  };

  useEffect(() => { refresh(); }, []);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      toast.success("✅ Notificaciones activadas");
      await refresh();
    } catch (err) {
      toast.error(err.message || "No se pudo activar");
    } finally { setLoading(false); }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      await unsubscribeFromPush();
      toast.success("Notificaciones desactivadas");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Error");
    } finally { setLoading(false); }
  };

  const handleTest = async () => {
    setLoading(true);
    try {
      const r = await sendTestPush();
      if ((r.sent || 0) > 0) toast.success(`📬 Enviado a ${r.sent} dispositivo(s)`);
      else toast.error(`No se pudo enviar (${r.failed || 0} fallos)`);
    } catch (err) {
      toast.error("Error enviando test");
    } finally { setLoading(false); }
  };

  if (!supported) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-400">
        <BellOff className="w-4 h-4 inline mr-1" />
        Tu navegador no soporta notificaciones push.
      </div>
    );
  }

  if (compact) {
    return (
      <Button
        onClick={status.subscribed ? handleUnsubscribe : handleSubscribe}
        disabled={loading}
        size="sm"
        data-testid="push-toggle-compact"
        className={`text-xs ${status.subscribed ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30" : "bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold"}`}
      >
        {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : (status.subscribed ? <BellRing className="w-3 h-3 mr-1" /> : <Bell className="w-3 h-3 mr-1" />)}
        {status.subscribed ? "Notificaciones ON" : "Activar avisos"}
      </Button>
    );
  }

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="push-settings">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${status.subscribed ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
          {status.subscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">Notificaciones móvil (PWA Push)</div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {status.subscribed
              ? "Estás recibiendo avisos en este dispositivo cuando un montaje se finaliza."
              : "Activa para recibir un \"ping\" en este móvil cuando termine un montaje y validar al vuelo."}
          </div>
          {status.permission === "denied" && (
            <div className="text-[11px] text-red-300 mt-1.5">
              ⚠️ Permiso bloqueado en el navegador. Reactívalo en Configuración del sitio.
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {!status.subscribed ? (
          <Button
            onClick={handleSubscribe}
            disabled={loading || status.permission === "denied"}
            data-testid="push-subscribe-btn"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Bell className="w-4 h-4 mr-1.5" />}
            Activar notificaciones
          </Button>
        ) : (
          <>
            <Button
              onClick={handleTest}
              disabled={loading}
              variant="outline"
              data-testid="push-test-btn"
              className="bg-transparent border-zinc-700">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
              Probar
            </Button>
            <Button
              onClick={handleUnsubscribe}
              disabled={loading}
              variant="outline"
              data-testid="push-unsubscribe-btn"
              className="bg-transparent border-red-500/30 text-red-300 hover:bg-red-500/10">
              <BellOff className="w-4 h-4 mr-1.5" />
              Desactivar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
